import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from '../../ui';
import { WarmupCard } from './WarmupCard';
import { panelVariants, scrimVariants } from '../../lib/motion';
import { sound } from '../../lib/sound';
import { useLocale } from '../../state/useLocale';
import type { PublicMission, Tutorial } from '../../types';

/**
 * The warm-up: the tutorial a level offers before it starts.
 *
 * It is optional in every direction. Esc closes it, Skip is on screen the
 * whole time, nothing here is graded, and a miss at the prompt costs nothing,
 * because a learner who already knows the tools should be able to leave in
 * one keystroke. The Hud keeps a way back in for the rest of the level.
 *
 * One card at a time, because the point is one tool at a time. The rail
 * across the top is the only thing that says how much is left: it is
 * decoration for the eye and hidden from assistive technology, which is told
 * the step in words instead.
 */

export interface WarmupProps {
  open: boolean;
  mission: PublicMission;
  tutorial: Tutorial;
  /**
   * Called by Skip, Esc, the scrim, and "Start the level". All of them mark it
   * seen, and the reason separates the last one from the other three: a deck
   * read to the end and a deck dismissed on the first card are the same event
   * to this component and opposite answers to the question of whether the
   * warm-up is worth writing more of (docs/analytics.md).
   */
  onClose(reason: 'done' | 'skip'): void;
}

export function Warmup({ open, mission, tutorial, onClose }: WarmupProps) {
  const { dir, strings } = useLocale();
  const s = strings.warmup;
  const [index, setIndex] = useState(0);
  /** Steps whose rep has been typed correctly. Kept across a close: it is a record of practice. */
  const [passed, setPassed] = useState<ReadonlySet<number>>(() => new Set<number>());
  const [wasOpen, setWasOpen] = useState(open);

  const panelRef = useRef<HTMLDivElement>(null);
  const footRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  const cards = tutorial.cards;
  const total = cards.length;
  const card = cards[index];
  const isLast = index === total - 1;

  // Worked out during render rather than in an effect, the way MissionBrief
  // reads a new grade: by the time effects run, the first card has mounted
  // and its prompt has taken focus, so both of these would be too late. A
  // reopened warm-up starts at the first card — it is something to read
  // through, not a place you were standing.
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setIndex(0);
      restoreTo.current = document.activeElement as HTMLElement | null;
    }
  }

  const go = useCallback(
    (delta: number) => {
      setIndex((i) => Math.min(total - 1, Math.max(0, i + delta)));
      sound.click();
    },
    [total],
  );

  const advance = useCallback(() => {
    if (isLast) onClose('done');
    else go(1);
  }, [isLast, go, onClose]);

  const handlePass = useCallback(() => {
    setPassed((prev) => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
    // The rep is done and the only thing left on this card is to move on, so
    // put the learner on it. The button is found rather than held in a ref
    // because the kit's Button takes a class, not a ref.
    footRef.current?.querySelector<HTMLButtonElement>('.warmup-next')?.focus();
  }, [index]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const active = document.activeElement as HTMLElement | null;
    // Child effects run before this one, so a card with a rep has already
    // claimed focus for its prompt, which is a better place to land than the
    // dialog itself. Only take focus when nothing inside the panel holds it.
    if (panel && (!active || !panel.contains(active))) panel.focus();
    return () => restoreTo.current?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose('skip');
        return;
      }

      // At the prompt the arrow keys belong to the caret, not to the deck.
      const target = e.target;
      const composing = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
      if (!composing && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        // The deck lays out the way the page reads, so the key for "the next
        // card" is the one pointing at the side the next card comes from:
        // right in English, left in Arabic (docs/i18n.md §7).
        const forward = (e.key === 'ArrowRight') === (dir === 'ltr');
        go(forward ? 1 : -1);
        return;
      }

      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      // The panel holds focus on open and is deliberately not in that list,
      // so without this the first Shift+Tab escapes behind the scrim and the
      // trap can never take it back. Same reasoning as ClearOverlay.
      const active = document.activeElement as HTMLElement | null;
      if (!active || active === panel || !panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, go, dir]);

  return (
    <AnimatePresence>
      {open && card && (
        <motion.div
          className="warmup-scrim"
          variants={scrimVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose('skip');
          }}
        >
          <motion.div
            className="warmup"
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="warmup-title"
            variants={panelVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <header className="warmup-head">
              <div className="warmup-id">
                <span className="label">{s.idLabel(mission.level)}</span>
                <ol className="warmup-rail" aria-hidden="true">
                  {cards.map((c, i) => (
                    <li
                      key={c.tool + i}
                      className={[i < index || passed.has(i) ? 'is-done' : '', i === index ? 'is-now' : '']
                        .filter(Boolean)
                        .join(' ')}
                    />
                  ))}
                </ol>
              </div>
              <h2 className="warmup-title" id="warmup-title">
                {tutorial.title ?? s.title}
              </h2>
              {tutorial.lede && <p className="warmup-lede">{tutorial.lede}</p>}
              <p className="warmup-step" role="status" aria-live="polite">
                {s.step(index + 1, total, card.label ?? card.tool)}
              </p>
            </header>

            <div className="warmup-stage scroll">
              <AnimatePresence mode="wait" initial={false}>
                <WarmupCard
                  key={index}
                  card={card}
                  cwd={mission.cwd}
                  passed={passed.has(index)}
                  onPass={handlePass}
                />
              </AnimatePresence>
            </div>

            <footer className="warmup-foot" ref={footRef}>
              <Button variant="quiet" onClick={() => onClose('skip')} title={s.skipTitle}>
                {s.skip}
              </Button>
              <div className="warmup-nav">
                <Button variant="quiet" onClick={() => go(-1)} disabled={index === 0}>
                  {s.back}
                </Button>
                <Button variant="solid" className="warmup-next" onClick={advance}>
                  {isLast ? s.start : s.next}
                </Button>
              </div>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Warmup;
